import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    createCatalogProductOfferBlock,
    actOnCatalogAttributeCandidate,
    createCatalogProductAttribute,
    createCatalogProductKeyword,
    approveCatalogProductKeyword,
    approvePendingCatalogAiKeywords,
    deleteCatalogProductAttribute,
    deleteCatalogProductKeyword,
    deactivateCatalogOfferBlock,
    getCatalogCategories,
    getCatalogAttributeDefinitions,
    createCatalogAttributeDefinition, updateCatalogAttributeDefinition, getCatalogAttributeValues, createCatalogAttributeValue, updateCatalogAttributeValue, getCatalogAttributeMappingInfo, getCatalogSupplierAttributeUnresolved, importCatalogAttributeDefinitionsXml, importCatalogAiKeywords,
    createCatalogSupplierAttributeRule, createCatalogSupplierAttributeValueMapping,
    getCatalogAttributeRules, patchCatalogSupplierAttributeRule, runCatalogAttributeIngestion, mergeCatalogAttributeDefinition,
    downloadCatalogPromAiEnrichment,
    getCatalogOfferBlocks,
    getCatalogProductDetail,
    getCatalogWholesaleMappings, updateCatalogWholesaleMapping,
    makeCatalogImageMain,
    getCatalogProducts,
    runCatalogBulkAction,
    updateCatalogCategory,
    updateCatalogProductAttribute,
    updateCatalogProductKeyword,
    updateCatalogMapping,
    updateCatalogProduct,
    createCatalogAdditionalBarcode, updateCatalogAdditionalBarcode,
    updateCatalogImageMarketplaceAccess,
    uploadCatalogImage, deleteCatalogImage, reorderCatalogImages,
    saveManualCatalogContent, deleteManualCatalogContent,
    saveCatalogManualFields, clearCatalogManualFields,
} from "../api/catalogApi";
import { getPromAccounts } from "../api/promApi";
import "./CatalogPage.css";

const pageStyle = {
    padding: "24px",
    display: "grid",
    gap: "16px",
    maxWidth: "1700px",
    margin: "0 auto",
    boxSizing: "border-box",
};

const tabs = [
    { key: "products", label: "Товары" },
    { key: "categories", label: "Категории" },
    { key: "attribute_normalization", label: "Характеристики" },
    { key: "export", label: "Экспорт, Импорт КС" },
    { key: "blocks", label: "Блокировки" },
    { key: "archive", label: "Архив" },
];

const productDrawerTabs = [
    "Основное",
    "Штрихкоды",
    "Контент",
    "Ключевые слова",
    "Характеристики",
    "Фото",
    "Категория",
    "Поставщики",
    "Остатки и цены",
    "Блокировки",
    "Экспорт",
    "Качество и история",
];

const formatDate = (value) => {
    if (!value) {
        return "—";
    }
    try {
        return new Date(value).toLocaleString("ru-RU");
    } catch (error) {
        return String(value);
    }
};

const textOrDash = (value) => {
    const text = String(value ?? "").trim();
    return text || "—";
};

const issueText = (quality) => {
    const issues = quality?.issues || [];
    return issues.length ? issues.join(", ") : "Проблем нет";
};

const statusPillClass = (active) => `cat-pill ${active ? "g" : "r"}`;

const offerBlockStatusLabel = (status) => {
    if (status === "active") {
        return "Активна";
    }
    if (status === "expired") {
        return "Истекла";
    }
    if (status === "inactive") {
        return "Снята";
    }
    return status || "—";
};

const offerBlockPillClass = (status) => `cat-pill ${status === "active" ? "g" : status === "expired" ? "warn" : "off"}`;

const toDateTimeLocalValue = (value) => {
    if (!value) {
        return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "";
    }
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
};

const defaultBlockUntil = () => {
    const date = new Date();
    date.setHours(date.getHours() + 24);
    return toDateTimeLocalValue(date.toISOString());
};

const Field = ({ label, children }) => (
    <label style={{ display: "grid", gap: "5px" }}>
        <span style={{ color: "var(--cat-muted)", fontSize: "12px", fontWeight: 800 }}>{label}</span>
        {children}
    </label>
);

const CategoryTree = ({ categories, selectedCode, onSelect }) => {
    const renderNode = (node, depth = 0) => (
        <div key={node.category_code}>
            <button
                type="button"
                onClick={() => onSelect(node.category_code)}
                className={`cat-tree-item${selectedCode === node.category_code ? " sel" : ""}`}
                style={{ paddingLeft: `${8 + depth * 14}px`, opacity: node.is_active ? 1 : 0.55 }}
            >
                <span className="arrow">{node.children?.length ? "▾" : "•"}</span>
                {" "}
                {node.name_ua}
                <span className="cnt"> ({node.products_count || 0})</span>
            </button>
            {(node.children || []).map((child) => renderNode(child, depth + 1))}
        </div>
    );

    return <div className="cat-tree">{categories.map((node) => renderNode(node))}</div>;
};

const CatalogPage = () => {
    const [activeTab, setActiveTab] = useState("products");
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [products, setProducts] = useState({ items: [], total: 0, limit: 50, offset: 0 });
    const [productFilters, setProductFilters] = useState({
        q: "",
        archived: "active",
        quality_filter: "",
        mapping_status: "",
        mapping_source: "",
        moderation_status: "",
        sort: "sales_rank",
    });
    const [selectedSkus, setSelectedSkus] = useState([]);
    const [copiedSku, setCopiedSku] = useState(null);
    const [drawerSku, setDrawerSku] = useState(null);
    const [drawerData, setDrawerData] = useState(null);
    const [drawerWholesaleMappings, setDrawerWholesaleMappings] = useState([]);
    const [drawerTab, setDrawerTab] = useState("Основное");
    const [additionalBarcodeValue, setAdditionalBarcodeValue] = useState("");
    const [draft, setDraft] = useState({});
    const [attributeDefinitions, setAttributeDefinitions] = useState([]);
    const [normalizationSection, setNormalizationSection] = useState("definitions");
    const [selectedDefinitionId, setSelectedDefinitionId] = useState("");
    const [canonicalValues, setCanonicalValues] = useState([]);
    const [definitionDraft, setDefinitionDraft] = useState({ code: "", name_ua: "", name_ru: "", value_type: "text", default_unit: "" });
    const [valueDraft, setValueDraft] = useState("");
    const [valueRuDraft, setValueRuDraft] = useState("");
    const [definitionSearch, setDefinitionSearch] = useState("");
    const [mappingInfo, setMappingInfo] = useState(null);
    const [unresolvedAttributes, setUnresolvedAttributes] = useState({ fields: [], values: [] });
    const [attributeRules, setAttributeRules] = useState([]);
    const [ruleTargets, setRuleTargets] = useState({});
    const [ingestStats, setIngestStats] = useState(null);
    const [mergeTargetId, setMergeTargetId] = useState("");
    const [mergeBusy, setMergeBusy] = useState(false);
    const [unresolvedVisible, setUnresolvedVisible] = useState(10);
    const [characteristicsTab, setCharacteristicsTab] = useState("main");
    const [definitionSearchOpen, setDefinitionSearchOpen] = useState(false);
    const [rulesCollapsed, setRulesCollapsed] = useState(false);
    const [xmlFile, setXmlFile] = useState(null);
    const [definitionEdit, setDefinitionEdit] = useState({ name_ua: "", name_ru: "", default_unit: "" });
    const [attributeDraft, setAttributeDraft] = useState({ definition_name_ua: "", values: "", unit: "", value_type: "text" });
    const [attributeEdits, setAttributeEdits] = useState({});
    const [keywordDrafts, setKeywordDrafts] = useState({ ua: "", ru: "" });
    const [keywordEdits, setKeywordEdits] = useState({});
    const [promAccounts, setPromAccounts] = useState([]);
    const [aiExportAccountId, setAiExportAccountId] = useState("");
    const [aiExportBusy, setAiExportBusy] = useState(false);
    const [aiKeywordFile, setAiKeywordFile] = useState(null);
    const [aiKeywordImportBusy, setAiKeywordImportBusy] = useState(false);
    const [aiKeywordImportResult, setAiKeywordImportResult] = useState(null);
    const [offerBlocks, setOfferBlocks] = useState({ items: [], total: 0, limit: 50, offset: 0 });
    const [offerBlockFilters, setOfferBlockFilters] = useState({
        q: "",
        activity: "active",
        supplier_code: "",
    });
    const [blockDraft, setBlockDraft] = useState({
        supplier_code: "",
        blocked_until: defaultBlockUntil(),
        reason: "",
        comment: "",
    });
    const [categoryDraft, setCategoryDraft] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    const categoryOptions = useMemo(() => {
        const rows = [];
        const walk = (nodes, prefix = "") => {
            nodes.forEach((node) => {
                const label = prefix ? `${prefix} / ${node.name_ua}` : node.name_ua;
                rows.push({ code: node.category_code, label, level_no: node.level_no });
                walk(node.children || [], label);
            });
        };
        walk(categories);
        return rows;
    }, [categories]);

    const selectedCategoryNode = useMemo(() => {
        let found = null;
        const walk = (nodes) => {
            nodes.forEach((node) => {
                if (node.category_code === selectedCategory) {
                    found = node;
                }
                walk(node.children || []);
            });
        };
        walk(categories);
        return found;
    }, [categories, selectedCategory]);

    const blockSupplierOptions = useMemo(() => {
        const seen = new Set();
        const rows = [];
        (drawerData?.stock_prices || []).forEach((row) => {
            const code = String(row.supplier_code || "").trim();
            if (!code || seen.has(code)) {
                return;
            }
            seen.add(code);
            rows.push({ code, label: textOrDash(row.supplier_name || code) });
        });
        return rows;
    }, [drawerData]);

    const loadCategories = async () => {
        const data = await getCatalogCategories();
        setCategories(data || []);
    };

    const loadProducts = useCallback(async (offset = 0, overrides = {}) => {
        setLoading(true);
        try {
            const data = await getCatalogProducts({
                ...productFilters,
                ...(activeTab === "archive" ? { archived: "archived" } : {}),
                ...overrides,
                category_code: selectedCategory,
                limit: 50,
                offset,
            });
            setProducts(data);
        } finally {
            setLoading(false);
        }
    }, [activeTab, productFilters, selectedCategory]);

    const loadDrawer = async (sku) => {
        if (!sku) {
            setDrawerData(null);
            return;
        }
            const [data, wholesaleMappings] = await Promise.all([getCatalogProductDetail(sku), getCatalogWholesaleMappings(sku)]);
            setDrawerData(data);
            setDrawerWholesaleMappings(wholesaleMappings || []);
        setAttributeEdits(Object.fromEntries((data.attributes || []).map((row) => [row.id, {
            values: (row.values || []).join(", "),
            unit: row.unit || "",
        }])));
        setKeywordEdits(Object.fromEntries((data.keywords || []).map((row) => [row.id, row.keyword || ""])));
        setDraft({
            tabletki_guid: data.tabletki_guid || "",
            barcode: data.product?.barcode || "",
            manufacturer: data.product?.manufacturer || "",
            name_ua: data.product?.name_ua || "",
            name_ru: data.product?.name_ru || "",
            category_l1_code: data.product?.category_l1_code || "",
            category_l2_code: data.product?.category_l2_code || "",
            weight_g: data.weight_g || "",
            length_mm: data.length_mm || "",
            width_mm: data.width_mm || "",
            height_mm: data.height_mm || "",
            volume_ml: data.volume_ml || "",
            description_ua: data.description_ua || "",
            description_ru: data.description_ru || "",
            main_image_url: data.product?.main_image_url || "",
            is_archived: Boolean(data.product?.is_archived),
            archived_reason: data.product?.archived_reason || "",
        });
    };

    const copySku = async (sku) => {
        try {
            await navigator.clipboard.writeText(String(sku));
            setCopiedSku(sku);
            setMessage(`SKU ${sku} скопирован`);
            window.setTimeout(() => setCopiedSku((current) => (current === sku ? null : current)), 1800);
        } catch (error) {
            setMessage("Не удалось скопировать SKU. Проверьте разрешение браузера на буфер обмена.");
        }
    };

    const loadAttributeDefinitions = async () => {
        const data = await getCatalogAttributeDefinitions({ limit: 200, active_only: true });
        setAttributeDefinitions(data || []);
    };
    const loadUnresolvedAttributes = useCallback(async () => {
        setUnresolvedAttributes(await getCatalogSupplierAttributeUnresolved());
    }, []);
    const loadAttributeRules = useCallback(async () => {
        setAttributeRules(await getCatalogAttributeRules({}));
    }, []);

    const loadCanonicalValues = async (definitionId = selectedDefinitionId) => { if (definitionId) setCanonicalValues(await getCatalogAttributeValues(definitionId)); else setCanonicalValues([]); };

    const loadOfferBlocks = useCallback(async (offset = 0) => {
        setLoading(true);
        try {
            const data = await getCatalogOfferBlocks({
                ...offerBlockFilters,
                limit: 50,
                offset,
            });
            setOfferBlocks(data);
        } finally {
            setLoading(false);
        }
    }, [offerBlockFilters]);

    useEffect(() => {
        loadCategories().catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить категории"));
        loadAttributeDefinitions().catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить справочник характеристик"));
    }, []);

    useEffect(() => {
        if (activeTab === "products" || activeTab === "archive") {
            loadProducts(0).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить товары"));
        }
    }, [activeTab, selectedCategory, loadProducts]);

    useEffect(() => {
        if (activeTab === "attribute_normalization" && characteristicsTab === "rules") {
            setUnresolvedVisible(10);
            loadUnresolvedAttributes().catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить неразобранные поля"));
            loadAttributeRules().catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить правила"));
        }
    }, [activeTab, characteristicsTab, loadUnresolvedAttributes, loadAttributeRules]);

    useEffect(() => {
        if (!selectedCategoryNode) {
            setCategoryDraft(null);
            return;
        }
        setCategoryDraft({
            parent_category_code: selectedCategoryNode.parent_category_code || "",
            name_ua: selectedCategoryNode.name_ua || "",
            name_ru: selectedCategoryNode.name_ru || "",
            level_no: selectedCategoryNode.level_no ?? "",
            is_active: Boolean(selectedCategoryNode.is_active),
        });
    }, [selectedCategoryNode]);

    useEffect(() => {
        if (drawerSku) {
            loadDrawer(drawerSku).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить карточку"));
        }
    }, [drawerSku]);

    useEffect(() => {
        if (activeTab === "export") {
            getPromAccounts()
                .then((accounts) => {
                    const activeAccounts = (accounts || []).filter((account) => account.is_active);
                    setPromAccounts(activeAccounts);
                    setAiExportAccountId((currentId) => currentId || String(activeAccounts[0]?.id || ""));
                })
                .catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить кабинеты Prom"));
        }
        if (activeTab === "blocks") {
            loadOfferBlocks(0).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить блокировки"));
        }
    }, [activeTab, loadOfferBlocks]);

    const downloadAiEnrichmentJsonl = async () => {
        setAiExportBusy(true);
        try {
            const response = await downloadCatalogPromAiEnrichment(Number(aiExportAccountId));
            const disposition = response.headers["content-disposition"] || "";
            const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || "prom_ai_enrichment.jsonl";
            const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/x-ndjson" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setMessage("Файл JSONL скачан");
        } catch (error) {
            let detail = error?.response?.data?.detail;
            if (!detail && error?.response?.data instanceof Blob) {
                try {
                    detail = JSON.parse(await error.response.data.text()).detail;
                } catch (_) {
                    // Keep the generic message for non-JSON download errors.
                }
            }
            setMessage(detail || "Не удалось выгрузить JSONL для AI");
        } finally {
            setAiExportBusy(false);
        }
    };

    const importAiKeywordsJsonl = async () => {
        if (!aiKeywordFile) {
            setMessage("Выберите JSONL-файл с ключевыми словами");
            return;
        }
        setAiKeywordImportBusy(true);
        try {
            const result = await importCatalogAiKeywords(aiKeywordFile);
            setAiKeywordImportResult(result);
            setMessage(`Импорт завершён: создано ${result.keywords_created} keywords`);
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось импортировать keywords JSONL");
        } finally {
            setAiKeywordImportBusy(false);
        }
    };

    const saveProduct = async (extra = {}) => {
        if (!drawerSku) {
            return;
        }
        const payload = { ...draft, ...extra };
        ["weight_g", "length_mm", "width_mm", "height_mm", "volume_ml"].forEach((field) => {
            if (String(payload[field] ?? "").trim() === "") {
                payload[field] = null;
            }
        });
        if (payload.is_archived && !String(payload.archived_reason || "").trim()) {
            setMessage("Для архивации укажите причину");
            return;
        }
        try {
            const data = await updateCatalogProduct(drawerSku, payload);
            setDrawerData(data);
            setMessage("Карточка сохранена");
            await loadProducts(products.offset || 0);
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось сохранить карточку");
        }
    };

    const refreshDrawer = async () => {
        await loadDrawer(drawerSku);
    };

    const updateImageMarketplaceAccess = async (image, isMarketplaceAllowed) => {
        try {
            const data = await updateCatalogImageMarketplaceAccess(drawerSku, image.id, isMarketplaceAllowed);
            setDrawerData(data);
            setDraft((current) => ({ ...current, main_image_url: data.product?.main_image_url || "" }));
            setMessage(isMarketplaceAllowed ? "Фото разрешено для маркетплейсов" : "Фото исключено из выгрузки на маркетплейсы");
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось изменить доступность фото");
        }
    };

    const makeImageMain = async (image) => {
        try {
            const data = await makeCatalogImageMain(drawerSku, image.id);
            setDrawerData(data);
            setDraft((current) => ({ ...current, main_image_url: data.product?.main_image_url || "" }));
            setMessage("Главное фото обновлено");
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось назначить главное фото");
        }
    };

    const uploadImages = async (files) => {
        try {
            for (const file of Array.from(files || [])) {
                await uploadCatalogImage(drawerSku, file);
            }
            await refreshDrawer();
            setMessage("Фото загружены");
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось загрузить фото");
        }
    };

    const saveManualContent = async (languageCode) => {
        try {
            const data = await saveManualCatalogContent(drawerSku, { language_code: languageCode, description: draft[`description_${languageCode}`] || "" });
            setDrawerData(data);
            setMessage(`Ручное описание ${languageCode.toUpperCase()} сохранено`);
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось сохранить описание");
        }
    };

    const saveManualFields = async () => {
        const fields = ["manufacturer", "barcode", "name_ua", "name_ru", "weight_g", "length_mm", "width_mm", "height_mm", "volume_ml"];
        try {
            const values = Object.fromEntries(fields.filter((field) => String(draft[field] ?? "").trim() !== "").map((field) => [field, draft[field]]));
            if (!Object.keys(values).length) {
                setMessage("Заполните хотя бы одно поле для ручного сохранения");
                return;
            }
            const data = await saveCatalogManualFields(drawerSku, values);
            setDrawerData(data);
            setMessage("Ручные поля закреплены: импорт их не перезапишет");
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось сохранить ручные поля");
        }
    };

    const clearManualField = async (field) => {
        try {
            const data = await clearCatalogManualFields(drawerSku, [field]);
            setDrawerData(data);
            setMessage("Ручной override снят; значение обновится при следующем импорте");
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось снять override");
        }
    };

    const createAttribute = async () => {
        const values = String(attributeDraft.values || "").split(",").map((value) => value.trim()).filter(Boolean);
        if (!String(attributeDraft.definition_name_ua || "").trim() || !values.length) {
            setMessage("Укажите характеристику и хотя бы одно значение");
            return;
        }
        await createCatalogProductAttribute(drawerSku, {
            definition_name_ua: attributeDraft.definition_name_ua,
            values,
            unit: attributeDraft.unit || null,
            value_type: attributeDraft.value_type,
        });
        setAttributeDraft({ definition_name_ua: "", values: "", unit: "", value_type: "text" });
        await refreshDrawer();
        await loadAttributeDefinitions();
        setMessage("Характеристика добавлена");
    };

    const saveAttribute = async (attributeId) => {
        const edit = attributeEdits[attributeId] || {};
        const values = String(edit.values || "").split(",").map((value) => value.trim()).filter(Boolean);
        if (!values.length) {
            setMessage("Укажите хотя бы одно значение характеристики");
            return;
        }
        await updateCatalogProductAttribute(drawerSku, attributeId, { values, unit: edit.unit || null });
        await refreshDrawer();
        setMessage("Характеристика сохранена");
    };

    const createKeywords = async (languageCode) => {
        const values = String(keywordDrafts[languageCode] || "").split(",").map((value) => value.trim()).filter(Boolean);
        if (!values.length) {
            return;
        }
        for (const keyword of values) {
            await createCatalogProductKeyword(drawerSku, { keyword, language_code: languageCode });
        }
        setKeywordDrafts({ ...keywordDrafts, [languageCode]: "" });
        await refreshDrawer();
        setMessage("Ключевые слова добавлены");
    };

    const saveKeyword = async (keywordId) => {
        const keyword = String(keywordEdits[keywordId] || "").trim();
        if (!keyword) {
            setMessage("Ключевое слово не может быть пустым");
            return;
        }
        await updateCatalogProductKeyword(drawerSku, keywordId, { keyword });
        await refreshDrawer();
        setMessage("Ключевое слово сохранено");
    };

    const moveKeyword = async (row, direction) => {
        const group = (drawerData.keywords || []).filter((item) => item.language_code === row.language_code && item.platform === row.platform);
        const index = group.findIndex((item) => item.id === row.id);
        const target = group[index + direction];
        if (!target) {
            return;
        }
        await Promise.all([
            updateCatalogProductKeyword(drawerSku, row.id, { sort_order: target.sort_order }),
            updateCatalogProductKeyword(drawerSku, target.id, { sort_order: row.sort_order }),
        ]);
        await refreshDrawer();
    };

    const approveKeyword = async (keywordId) => {
        await approveCatalogProductKeyword(drawerSku, keywordId);
        await refreshDrawer();
        setMessage("Ключевое слово подтверждено");
    };

    const approveAllPendingAiKeywords = async () => {
        const result = await approvePendingCatalogAiKeywords(drawerSku);
        await refreshDrawer();
        setMessage(`Подтверждено ключевых слов: ${result.approved}`);
    };

    const createOfferBlock = async (overrides = {}) => {
        if (!drawerSku) {
            return;
        }
        const payload = { ...blockDraft, ...overrides };
        if (!String(payload.reason || "").trim()) {
            setMessage("Для блокировки укажите причину");
            return;
        }
        if (!payload.blocked_until) {
            setMessage("Укажите срок блокировки");
            return;
        }
        await createCatalogProductOfferBlock(drawerSku, {
            supplier_code: payload.supplier_code || null,
            blocked_until: new Date(payload.blocked_until).toISOString(),
            reason: payload.reason,
            comment: payload.comment || null,
        });
        setMessage("Блокировка сохранена");
        setBlockDraft({
            supplier_code: "",
            blocked_until: defaultBlockUntil(),
            reason: "",
            comment: "",
        });
        await loadDrawer(drawerSku);
        if (activeTab === "blocks") {
            await loadOfferBlocks(offerBlocks.offset || 0);
        }
    };

    const deactivateOfferBlock = async (blockId) => {
        await deactivateCatalogOfferBlock(blockId);
        setMessage("Блокировка снята");
        if (drawerSku) {
            await loadDrawer(drawerSku);
        }
        if (activeTab === "blocks") {
            await loadOfferBlocks(offerBlocks.offset || 0);
        }
    };

    const archiveSelected = async (archive) => {
        const reason = archive ? window.prompt("Причина архивации") : "";
        if (archive && !String(reason || "").trim()) {
            return;
        }
        const dryRun = await runCatalogBulkAction({
            mode: "dry_run",
            action: archive ? "archive" : "unarchive",
            skus: selectedSkus,
            archived_reason: reason,
        });
        const confirmed = window.confirm(`Предпросмотр изменений: ${dryRun.affected_count}. Применить?`);
        if (!confirmed) {
            return;
        }
        await runCatalogBulkAction({
            mode: "apply",
            action: archive ? "archive" : "unarchive",
            skus: selectedSkus,
            archived_reason: reason,
        });
        setSelectedSkus([]);
        await loadProducts(products.offset || 0);
    };

    const approveModerationSelected = async () => {
        const dryRun = await runCatalogBulkAction({
            mode: "dry_run",
            action: "approve_keywords",
            skus: selectedSkus,
        });
        const confirmed = window.confirm(`Будет подтверждено ключевых слов: ${dryRun.affected_count}. Применить?`);
        if (!confirmed) {
            return;
        }
        await runCatalogBulkAction({
            mode: "apply",
            action: "approve_keywords",
            skus: selectedSkus,
        });
        setSelectedSkus([]);
        await loadProducts(products.offset || 0);
    };

    const setSelectedCategoryForProducts = async () => {
        if (!selectedCategory) {
            setMessage("Выберите категорию в дереве слева");
            return;
        }
        const selected = categoryOptions.find((item) => item.code === selectedCategory);
        const dryRun = await runCatalogBulkAction({
            mode: "dry_run",
            action: "set_category",
            skus: selectedSkus,
            category_l1_code: selected?.level_no === 1 ? selectedCategory : "",
            category_l2_code: selected?.level_no !== 1 ? selectedCategory : "",
        });
        const confirmed = window.confirm(`Предпросмотр изменений: ${dryRun.affected_count}. Применить выбранную категорию?`);
        if (!confirmed) {
            return;
        }
        await runCatalogBulkAction({
            mode: "apply",
            action: "set_category",
            skus: selectedSkus,
            category_l1_code: selected?.level_no === 1 ? selectedCategory : "",
            category_l2_code: selected?.level_no !== 1 ? selectedCategory : "",
        });
        setSelectedSkus([]);
        await loadProducts(products.offset || 0);
    };

    const downloadVisibleRows = () => {
        const rows = products.items || [];
        const header = ["SKU", "Название", "Barcode", "Производитель", "Категория", "Статус", "Проблемы"];
        const csvRows = rows.map((row) => [
            row.sku,
            row.name_ua || row.name_ru || "",
            row.barcode || "",
            row.manufacturer || "",
            (row.category_path || []).join(" / "),
            row.is_archived ? "В архиве" : "Активен",
            issueText(row.quality),
        ]);
        const csv = [header, ...csvRows]
            .map((items) => items.map((item) => `"${String(item).replace(/"/g, '""')}"`).join(";"))
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "catalog-products.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    const renderProductFilters = (archiveMode = false) => (
        <div style={{ display: "grid", gridTemplateColumns: "2fr repeat(6, minmax(140px, 1fr)) auto auto", gap: "10px" }}>
            <input
                className="cat-inp"
                placeholder="Поиск товара: SKU, barcode, название, поставщик"
                value={productFilters.q}
                onChange={(event) => setProductFilters({ ...productFilters, q: event.target.value })}
            />
            <select
                className="cat-inp"
                value={archiveMode ? "archived" : productFilters.archived}
                disabled={archiveMode}
                onChange={(event) => setProductFilters({ ...productFilters, archived: event.target.value })}
            >
                <option value="active">Активные</option>
                <option value="archived">Архивные</option>
                <option value="all">Все</option>
            </select>
            <select
                className="cat-inp"
                value={productFilters.quality_filter}
                onChange={(event) => setProductFilters({ ...productFilters, quality_filter: event.target.value })}
            >
                <option value="">Заполненность</option>
                <option value="has_image">Есть фото</option>
                <option value="missing_image">Без фото</option>
                <option value="has_name">Есть название</option>
                <option value="missing_name">Без названия</option>
                <option value="has_description">Есть описание</option>
                <option value="missing_description">Без описания</option>
                <option value="missing_description_ua">Без описания (укр)</option>
                <option value="missing_description_ru">Без описания (рус)</option>
                <option value="name_too_long">Длинное название (&gt;110)</option>
                <option value="has_manufacturer">Есть производитель</option>
                <option value="missing_manufacturer">Без производителя</option>
                <option value="has_barcode">Есть barcode</option>
                <option value="missing_barcode">Без barcode</option>
                <option value="has_dimensions">Есть габариты</option>
                <option value="missing_dimensions">Без габаритов</option>
                <option value="has_category">Есть категория</option>
                <option value="missing_category">Без категории</option>
                <option value="has_supplier_mapping">Есть связь с поставщиком</option>
                <option value="missing_supplier_mapping">Без связи с поставщиком</option>
            </select>
            <select
                className="cat-inp"
                value={productFilters.mapping_status}
                onChange={(event) => setProductFilters({ ...productFilters, mapping_status: event.target.value })}
            >
                <option value="">Статус связей</option>
                <option value="all_confirmed">Все связи подтверждены</option>
                <option value="has_unconfirmed">Есть неподтвержденные</option>
                <option value="no_mappings">Нет связей</option>
                <option value="has_inactive">Есть отключенные связи</option>
            </select>
            <select
                className="cat-inp"
                value={productFilters.moderation_status}
                onChange={(event) => setProductFilters({ ...productFilters, moderation_status: event.target.value })}
            >
                <option value="">Статус модерации</option>
                <option value="pending">На модерации</option>
                <option value="approved">Подтверждено</option>
            </select>
            <select
                className="cat-inp"
                value={productFilters.sort}
                onChange={(event) => setProductFilters({ ...productFilters, sort: event.target.value })}
            >
                <option value="sku">Сортировка: SKU</option>
                <option value="name">Сортировка: название</option>
                <option value="updated_at">Сортировка: дата изменения</option>
                <option value="quality">Больше проблем сначала</option>
                <option value="sales_rank">Рейтинг: популярные сначала</option>
            </select>
            <input
                className="cat-inp"
                placeholder="Источник связи"
                value={productFilters.mapping_source}
                onChange={(event) => setProductFilters({ ...productFilters, mapping_source: event.target.value })}
            />
            <button className="cat-btn cat-btn-primary" onClick={() => loadProducts(0)}>
                Обновить
            </button>
            <button
                className="cat-btn cat-btn-ghost"
                onClick={() => {
                    setSelectedCategory("");
                    setProductFilters({
                        q: "",
                        archived: archiveMode ? "archived" : "active",
                        quality_filter: "",
                        mapping_status: "",
                        mapping_source: "",
                        moderation_status: "",
                        sort: "sales_rank",
                    });
                }}
            >
                Сбросить
            </button>
        </div>
    );

    const renderProductsTable = (archiveMode = false) => (
        <div style={{ display: "grid", gridTemplateColumns: "280px minmax(0, 1fr)", gap: "14px" }}>
            <aside className="cat-card" style={{ padding: "12px", alignSelf: "start", maxHeight: "760px", overflow: "auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "center", marginBottom: "10px" }}>
                    <strong>Категории</strong>
                    <button className="cat-btn cat-btn-ghost cat-btn-sm" onClick={() => setSelectedCategory("")}>
                        Все
                    </button>
                </div>
                <CategoryTree categories={categories} selectedCode={selectedCategory} onSelect={setSelectedCategory} />
            </aside>
            <section className="cat-card" style={{ overflow: "hidden" }}>
                <div style={{ padding: "12px", display: "flex", gap: "8px", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <strong>{archiveMode ? "Архивные товары" : "Товары"}</strong>
                        <p className="cat-mute">Всего найдено: {products.total || 0}</p>
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button className="cat-btn cat-btn-ghost" disabled title="Создание товара будет добавлено после утверждения обязательных полей">
                            Создать товар
                        </button>
                        <button className="cat-btn cat-btn-ghost" onClick={downloadVisibleRows}>
                            Скачать список
                        </button>
                        {selectedSkus.length > 0 && (
                            <>
                                <button className="cat-btn cat-btn-ghost" onClick={setSelectedCategoryForProducts}>
                                    Сменить категорию
                                </button>
                                <button className="cat-btn cat-btn-ghost" onClick={approveModerationSelected}>
                                    Подтвердить модерацию
                                </button>
                                <button className={archiveMode ? "cat-btn cat-btn-primary" : "cat-btn cat-btn-danger"} onClick={() => archiveSelected(!archiveMode)}>
                                    {archiveMode ? "Разархивировать" : "Архивировать"}
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <div className="cat-tbl-wrap">
                    <table className="cat-tbl">
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={(products.items || []).length > 0 && (products.items || []).every((row) => selectedSkus.includes(row.sku))}
                                        onChange={(event) => {
                                            const pageSkus = (products.items || []).map((row) => row.sku);
                                            setSelectedSkus((current) =>
                                                event.target.checked
                                                    ? Array.from(new Set([...current, ...pageSkus]))
                                                    : current.filter((sku) => !pageSkus.includes(sku)),
                                            );
                                        }}
                                    />
                                </th>
                                <th>Фото</th>
                                <th>SKU</th>
                                <th>Название</th>
                                <th>Barcode</th>
                                <th>Производитель</th>
                                <th>Категория</th>
                                <th>Поставщики</th>
                                <th>Ключевые слова</th>
                                <th>Рейтинг (90 дн.)</th>
                                <th>Качество</th>
                                <th>Статус</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(products.items || []).map((row) => (
                                <tr key={row.sku} style={{ cursor: "pointer" }} onDoubleClick={() => setDrawerSku(row.sku)}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedSkus.includes(row.sku)}
                                            onChange={(event) => {
                                                setSelectedSkus((current) =>
                                                    event.target.checked
                                                        ? [...current, row.sku]
                                                        : current.filter((item) => item !== row.sku),
                                                );
                                            }}
                                        />
                                    </td>
                                    <td>
                                        {row.main_image_url ? (
                                            <img src={row.main_image_url} alt="" style={{ width: "44px", height: "44px", objectFit: "cover", borderRadius: "6px" }} />
                                        ) : (
                                            "—"
                                        )}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="cat-btn cat-btn-ghost cat-btn-sm"
                                            onClick={() => copySku(row.sku)}
                                            title="Скопировать SKU"
                                            aria-label={copiedSku === row.sku ? `SKU ${row.sku} скопирован` : `Скопировать SKU ${row.sku}`}
                                        >
                                            {row.sku}{copiedSku === row.sku ? " ✓" : " ⧉"}
                                        </button>
                                    </td>
                                    <td>
                                        <button style={{ border: 0, padding: 0, background: "transparent", color: "#1d4ed8", cursor: "pointer", textAlign: "left", font: "inherit" }} onClick={() => setDrawerSku(row.sku)}>
                                            {textOrDash(row.name_ua || row.name_ru)}
                                        </button>
                                    </td>
                                    <td>{textOrDash(row.barcode)}</td>
                                    <td>{textOrDash(row.manufacturer)}</td>
                                    <td>{(row.category_path || []).join(" / ") || "—"}</td>
                                    <td>
                                        Активных: {row.active_supplier_mappings_count}
                                        <br />
                                        Неподтв.: {row.unconfirmed_supplier_mappings_count}
                                    </td>
                                    <td>
                                        {row.keywords_moderation_status === "pending" ? (
                                            <span className="cat-pill warn">На модерации ({row.pending_ai_keywords_count})</span>
                                        ) : row.keywords_moderation_status === "approved" ? (
                                            <span className={statusPillClass(true)}>Подтверждено</span>
                                        ) : "—"}
                                    </td>
                                    <td>
                                        {row.sales_90d_rank ? `#${row.sales_90d_rank} · ${Number(row.sales_90d_quantity || 0).toLocaleString("ru-RU")} шт.` : "—"}
                                    </td>
                                    <td>{issueText(row.quality)}</td>
                                    <td>
                                        <span className={statusPillClass(!row.is_archived)}>{row.is_archived ? "В архиве" : "Активен"}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ padding: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <button
                        className="cat-btn cat-btn-ghost"
                        disabled={(products.offset || 0) <= 0}
                        onClick={() => loadProducts(Math.max(0, (products.offset || 0) - (products.limit || 50)))}
                    >
                        Назад
                    </button>
                    <span className="cat-mute">
                        {products.offset + 1}–{Math.min(products.offset + products.limit, products.total)} из {products.total}
                    </span>
                    <button
                        className="cat-btn cat-btn-ghost"
                        disabled={(products.offset || 0) + (products.limit || 50) >= (products.total || 0)}
                        onClick={() => loadProducts((products.offset || 0) + (products.limit || 50))}
                    >
                        Далее
                    </button>
                </div>
            </section>
        </div>
    );

    const renderOfferBlocks = () => (
        <section className="cat-card" style={{ overflow: "hidden" }}>
            <div style={{ padding: "12px", display: "grid", gap: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "center" }}>
                    <div>
                        <strong>Временные блокировки товаров</strong>
                        <p className="cat-mute">Всего найдено: {offerBlocks.total || 0}</p>
                    </div>
                    <button className="cat-btn cat-btn-primary" onClick={() => loadOfferBlocks(0)}>
                        Обновить
                    </button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr minmax(160px, 1fr) minmax(140px, 1fr) auto", gap: "10px" }}>
                    <input
                        className="cat-inp"
                        placeholder="Поиск: SKU, название, причина"
                        value={offerBlockFilters.q}
                        onChange={(event) => setOfferBlockFilters({ ...offerBlockFilters, q: event.target.value })}
                    />
                    <select
                        className="cat-inp"
                        value={offerBlockFilters.activity}
                        onChange={(event) => setOfferBlockFilters({ ...offerBlockFilters, activity: event.target.value })}
                    >
                        <option value="active">Активные</option>
                        <option value="expired">Истекшие</option>
                        <option value="inactive">Снятые</option>
                        <option value="all">Все</option>
                    </select>
                    <input
                        className="cat-inp"
                        placeholder="Поставщик: D2"
                        value={offerBlockFilters.supplier_code}
                        onChange={(event) => setOfferBlockFilters({ ...offerBlockFilters, supplier_code: event.target.value })}
                    />
                    <button
                        className="cat-btn cat-btn-ghost"
                        onClick={() => {
                            setOfferBlockFilters({ q: "", activity: "active", supplier_code: "" });
                        }}
                    >
                        Сбросить
                    </button>
                </div>
            </div>
            <div className="cat-tbl-wrap">
                <table className="cat-tbl">
                    <thead>
                        <tr>
                            <th>SKU</th>
                            <th>Название</th>
                            <th>Поставщик</th>
                            <th>До</th>
                            <th>Причина</th>
                            <th>Статус</th>
                            <th>Создана</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(offerBlocks.items || []).map((row) => (
                            <tr key={row.id}>
                                <td>
                                    <button className="cat-btn cat-btn-ghost cat-btn-sm" onClick={() => setDrawerSku(row.product_code)}>
                                        {row.product_code}
                                    </button>
                                </td>
                                <td>{textOrDash(row.product_name)}</td>
                                <td>{row.supplier_code ? textOrDash(row.supplier_name || row.supplier_code) : "Все поставщики"}</td>
                                <td>{formatDate(row.blocked_until)}</td>
                                <td>{textOrDash(row.reason)}</td>
                                <td>
                                    <span className={offerBlockPillClass(row.status)}>{offerBlockStatusLabel(row.status)}</span>
                                </td>
                                <td>{formatDate(row.created_at)}</td>
                                <td>
                                    {row.status === "active" ? (
                                        <button className="cat-btn cat-btn-danger" onClick={() => deactivateOfferBlock(row.id)}>
                                            Снять
                                        </button>
                                    ) : (
                                        "—"
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div style={{ padding: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <button
                    className="cat-btn cat-btn-ghost"
                    disabled={(offerBlocks.offset || 0) <= 0}
                    onClick={() => loadOfferBlocks(Math.max(0, (offerBlocks.offset || 0) - (offerBlocks.limit || 50)))}
                >
                    Назад
                </button>
                <span className="cat-mute">
                    {(offerBlocks.total || 0) === 0
                        ? "0 из 0"
                        : `${offerBlocks.offset + 1}–${Math.min(offerBlocks.offset + offerBlocks.limit, offerBlocks.total)} из ${offerBlocks.total}`}
                </span>
                <button
                    className="cat-btn cat-btn-ghost"
                    disabled={(offerBlocks.offset || 0) + (offerBlocks.limit || 50) >= (offerBlocks.total || 0)}
                    onClick={() => loadOfferBlocks((offerBlocks.offset || 0) + (offerBlocks.limit || 50))}
                >
                    Далее
                </button>
            </div>
        </section>
    );

    const renderCategories = () => (
        <div style={{ display: "grid", gridTemplateColumns: "360px minmax(0, 1fr)", gap: "16px" }}>
            <section className="cat-card" style={{ padding: "14px" }}>
                <h2 style={{ marginTop: 0 }}>Дерево категорий</h2>
                <CategoryTree
                    categories={categories}
                    selectedCode={selectedCategory}
                    onSelect={setSelectedCategory}
                />
            </section>
            <section className="cat-card" style={{ padding: "14px" }}>
                <h2 style={{ marginTop: 0 }}>Детали категории</h2>
                {selectedCategoryNode ? (
                    <div style={{ display: "grid", gap: "12px", maxWidth: "620px" }}>
                        <Field label="Код категории">
                            <input className="cat-inp" value={selectedCategoryNode.category_code} readOnly />
                        </Field>
                        <Field label="Название UA">
                            <input
                                className="cat-inp"
                                value={categoryDraft?.name_ua ?? selectedCategoryNode.name_ua ?? ""}
                                onChange={(event) => setCategoryDraft({ ...(categoryDraft || {}), name_ua: event.target.value })}
                            />
                        </Field>
                        <Field label="Название RU">
                            <input
                                className="cat-inp"
                                value={categoryDraft?.name_ru ?? selectedCategoryNode.name_ru ?? ""}
                                onChange={(event) => setCategoryDraft({ ...(categoryDraft || {}), name_ru: event.target.value })}
                            />
                        </Field>
                        <Field label="Родитель">
                            <select
                                className="cat-inp"
                                value={categoryDraft?.parent_category_code ?? selectedCategoryNode.parent_category_code ?? ""}
                                onChange={(event) => setCategoryDraft({ ...(categoryDraft || {}), parent_category_code: event.target.value })}
                            >
                                <option value="">Без родителя</option>
                                {categoryOptions
                                    .filter((item) => item.code !== selectedCategoryNode.category_code)
                                    .map((item) => (
                                        <option key={item.code} value={item.code}>
                                            {item.label}
                                        </option>
                                    ))}
                            </select>
                        </Field>
                        <Field label="Уровень">
                            <input
                                className="cat-inp"
                                type="number"
                                value={categoryDraft?.level_no ?? selectedCategoryNode.level_no ?? ""}
                                onChange={(event) => setCategoryDraft({ ...(categoryDraft || {}), level_no: event.target.value })}
                            />
                        </Field>
                        <label style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <input
                                type="checkbox"
                                checked={categoryDraft?.is_active ?? selectedCategoryNode.is_active}
                                onChange={(event) => setCategoryDraft({ ...(categoryDraft || {}), is_active: event.target.checked })}
                            />
                            Активна
                        </label>
                        <button
                            className="cat-btn cat-btn-primary"
                            onClick={async () => {
                                await updateCatalogCategory(selectedCategoryNode.category_code, categoryDraft || {});
                                await loadCategories();
                                setMessage("Категория сохранена");
                            }}
                        >
                            Сохранить категорию
                        </button>
                    </div>
                ) : (
                    <p className="cat-mute">Выберите категорию слева.</p>
                )}
            </section>
        </div>
    );

    const renderAttributeNormalization = () => {
        const selectedDefinition = attributeDefinitions.find((row) => String(row.id) === String(selectedDefinitionId));
        const openDefinition = async (definitionId) => {
            const definition = attributeDefinitions.find((row) => String(row.id) === String(definitionId));
            if (!definition) return;
            setDefinitionEdit({ name_ua: definition.name_ua || "", name_ru: definition.name_ru || "", default_unit: definition.default_unit || "" });
            setSelectedDefinitionId(String(definitionId));
            setDefinitionSearchOpen(false);
            await loadCanonicalValues(definitionId);
            setNormalizationSection("definitions");
        };
        const searchText = definitionSearch.trim().toLocaleLowerCase();
        const matchingDefinitions = searchText
            ? attributeDefinitions.filter((row) => `${row.name_ua} ${row.name_ru || ""}`.toLocaleLowerCase().includes(searchText))
            : attributeDefinitions;
        const mergeTarget = attributeDefinitions.find((d) => String(d.id) === String(mergeTargetId));
        const subTabClass = (key) => `cat-tab${characteristicsTab === key ? " active" : ""}`;
        return <div style={{ display: "grid", gap: "16px" }}>
            <div className="cat-tabbar">
                <button className={subTabClass("main")} onClick={() => setCharacteristicsTab("main")}>Основное</button>
                <button className={subTabClass("rules")} onClick={() => setCharacteristicsTab("rules")}>Правила</button>
            </div>
            {characteristicsTab === "rules" && renderSupplierAttributeFields()}
            {characteristicsTab === "main" && <>
                <section className="cat-card" style={{ padding: "14px", display: "grid", gap: "8px" }}>
                    <h3 style={{ margin: 0 }}>Найти характеристику</h3>
                    <div style={{ position: "relative" }}>
                        <input
                            className="cat-inp"
                            placeholder="Нажмите и выберите из списка или введите: вік, форма, вага…"
                            value={definitionSearch}
                            onFocus={() => setDefinitionSearchOpen(true)}
                            onBlur={() => setTimeout(() => setDefinitionSearchOpen(false), 150)}
                            onChange={(event) => { setDefinitionSearch(event.target.value); setDefinitionSearchOpen(true); }}
                        />
                        {definitionSearchOpen && <div style={{ position: "absolute", zIndex: 30, top: "calc(100% + 4px)", left: 0, right: 0, maxHeight: "320px", overflowY: "auto", backgroundColor: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "6px", boxShadow: "0 12px 28px rgba(15,23,42,0.14)" }}>
                            {matchingDefinitions.map((definition) => <button key={definition.id} onMouseDown={(event) => event.preventDefault()} onClick={() => openDefinition(definition.id)} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", border: "none", borderBottom: "1px solid #eef2f7", background: "#ffffff", cursor: "pointer", fontSize: "14px" }}>
                                <b>{definition.name_ua}</b>{definition.name_ru ? ` / ${definition.name_ru}` : ""}
                            </button>)}
                            {matchingDefinitions.length === 0 && <div style={{ padding: "10px 12px" }}><span className="cat-mute">Ничего не найдено — создайте характеристику ниже.</span></div>}
                        </div>}
                    </div>
                </section>
                {selectedDefinition && <section className="cat-card" style={{ padding: "14px", display: "grid", gap: "10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center" }}><div><h3 style={{ margin: 0 }}>{selectedDefinition.name_ua} {selectedDefinition.name_ru ? `/ ${selectedDefinition.name_ru}` : ""}</h3><p className="cat-mute">Код: {selectedDefinition.code} · Тип: {selectedDefinition.value_type}</p></div><button className="cat-btn cat-btn-ghost" onClick={() => { setSelectedDefinitionId(""); setCanonicalValues([]); setValueDraft(""); setValueRuDraft(""); }}>Закрыть карточку</button></div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr .5fr auto", gap: "8px" }}><label style={{ display: "grid", gap: "4px" }}><span className="cat-mute">Название украинское</span><input className="cat-inp" value={definitionEdit.name_ua} onChange={(event) => setDefinitionEdit({ ...definitionEdit, name_ua: event.target.value })} /></label><label style={{ display: "grid", gap: "4px" }}><span className="cat-mute">Название русское</span><input className="cat-inp" placeholder="Введите, если отсутствует" value={definitionEdit.name_ru} onChange={(event) => setDefinitionEdit({ ...definitionEdit, name_ru: event.target.value })} /></label><label style={{ display: "grid", gap: "4px" }}><span className="cat-mute">Единица</span><input className="cat-inp" placeholder="—" value={definitionEdit.default_unit} onChange={(event) => setDefinitionEdit({ ...definitionEdit, default_unit: event.target.value })} /></label><button className="cat-btn cat-btn-ghost" onClick={() => updateCatalogAttributeDefinition(selectedDefinition.id, definitionEdit).then(async () => { await loadAttributeDefinitions(); await openDefinition(selectedDefinition.id); setMessage("Характеристика сохранена"); })}>Сохранить</button></div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "8px" }}><input className="cat-inp" placeholder="Значение украинское" value={valueDraft} onChange={(event) => setValueDraft(event.target.value)} /><input className="cat-inp" placeholder="Значение русское" value={valueRuDraft} onChange={(event) => setValueRuDraft(event.target.value)} /><button className="cat-btn cat-btn-primary" onClick={() => { if (!valueDraft.trim()) { setMessage("Укажите значение на украинском языке"); return; } createCatalogAttributeValue(selectedDefinition.id, { value_ua: valueDraft, value_ru: valueRuDraft || null }).then(() => { setValueDraft(""); setValueRuDraft(""); return loadCanonicalValues(selectedDefinition.id); }).catch((error) => setMessage(error?.response?.data?.detail || error?.message || "Не удалось добавить значение")); }}>Добавить значение</button></div>
                    <table className="cat-tbl"><thead><tr><th>Значение украинское</th><th>Значение русское</th><th>Статус</th></tr></thead><tbody>{canonicalValues.map((value) => <tr key={value.id}><td>{value.value_ua || value.value}</td><td>{value.value_ru || "—"}</td><td><button className="cat-btn cat-btn-ghost" onClick={() => updateCatalogAttributeValue(value.id, { is_active: !value.is_active }).then(() => loadCanonicalValues(selectedDefinition.id))}>{value.is_active ? "Отключить" : "Включить"}</button></td></tr>)}</tbody></table>
                    <div style={{ borderTop: "1px solid #eef2f7", paddingTop: "10px", display: "grid", gap: "6px" }}>
                        <span className="cat-mute">Слияние дублей: выберите характеристику-приёмник, затем нажмите кнопку. Все товары, значения и правила перенесутся в неё, а эта характеристика будет архивирована (исчезнет из выбора).</span>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                            <select className="cat-inp" style={{ width: "auto", minWidth: "220px" }} value={mergeTargetId} onChange={(event) => setMergeTargetId(event.target.value)} disabled={mergeBusy}>
                                <option value="">Слить в характеристику…</option>
                                {attributeDefinitions.filter((d) => String(d.id) !== String(selectedDefinition.id)).map((d) => <option key={d.id} value={d.id}>{d.name_ua}</option>)}
                            </select>
                            <button className="cat-btn cat-btn-soft-danger" disabled={!mergeTargetId || mergeBusy} onClick={() => {
                                if (!mergeTarget || !window.confirm(`Слить «${selectedDefinition.name_ua}» в «${mergeTarget.name_ua}»? Источник будет архивирован.`)) return;
                                setMergeBusy(true);
                                mergeCatalogAttributeDefinition(selectedDefinition.id, Number(mergeTargetId))
                                    .then(async (stats) => { setMergeTargetId(""); setSelectedDefinitionId(""); setDefinitionSearch(""); await loadAttributeDefinitions(); setMessage(`Готово. Слито в «${mergeTarget.name_ua}»: товаров ${stats.product_attrs_moved || 0}, значений ${stats.values_moved || 0}, правил ${stats.rules_moved || 0}. Характеристика архивирована.`); })
                                    .catch((error) => setMessage(error?.response?.data?.detail || "Не удалось слить характеристики"))
                                    .finally(() => setMergeBusy(false));
                            }}>{mergeBusy ? "Слияние…" : "Слить и архивировать"}</button>
                            {!mergeTargetId && !mergeBusy && <span className="cat-mute">← сначала выберите приёмник</span>}
                        </div>
                    </div>
                </section>}
                <section className="cat-card" style={{ padding: "14px", display: "grid", gap: "10px" }}>
                    <h3 style={{ margin: 0 }}>Новая характеристика</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr .7fr .5fr auto", gap: "8px" }}>
                        <input className="cat-inp" placeholder="Название украинское" value={definitionDraft.name_ua} onChange={(event) => setDefinitionDraft({ ...definitionDraft, name_ua: event.target.value })} />
                        <input className="cat-inp" placeholder="Название русское" value={definitionDraft.name_ru} onChange={(event) => setDefinitionDraft({ ...definitionDraft, name_ru: event.target.value })} />
                        <select className="cat-inp" value={definitionDraft.value_type} onChange={(event) => setDefinitionDraft({ ...definitionDraft, value_type: event.target.value })}><option value="text">Текст</option><option value="number">Число</option><option value="select">Выбор</option><option value="multiselect">Несколько значений</option></select>
                        <input className="cat-inp" placeholder="Ед." value={definitionDraft.default_unit} onChange={(event) => setDefinitionDraft({ ...definitionDraft, default_unit: event.target.value })} />
                        <button className="cat-btn cat-btn-primary" onClick={() => createCatalogAttributeDefinition(definitionDraft).then(async (row) => { await loadAttributeDefinitions(); await openDefinition(row.id); setMessage("Характеристика создана"); }).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось создать характеристику"))}>Сохранить</button>
                    </div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <input type="file" accept=".xml,text/xml" onChange={(event) => setXmlFile(event.target.files?.[0] || null)} />
                        <button className="cat-btn cat-btn-ghost" disabled={!xmlFile} onClick={() => importCatalogAttributeDefinitionsXml(xmlFile).then(async (result) => { await loadAttributeDefinitions(); setMessage(`Импорт: характеристик ${result.imported_definitions}, значений ${result.imported_values}, дублей пропущено автоматически`); }).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось импортировать XML"))}>Импортировать справочник из XML</button>
                    </div>
                </section>
            </>}
        </div>;
    };

    const renderSupplierAttributeFields = () => {
        const queue = unresolvedAttributes.fields || [];
        const shown = queue.slice(0, unresolvedVisible);
        const dropFromQueue = (sourceName) => setUnresolvedAttributes((prev) => ({ ...prev, fields: (prev.fields || []).filter((f) => f.source_name !== sourceName) }));
        const handleRule = (row, action) => {
            const definitionId = ruleTargets[row.source_name];
            if (action === "map" && !definitionId) { setMessage(`Сначала выберите характеристику для «${row.source_name}»`); return; }
            createCatalogSupplierAttributeRule({ supplier_id: null, source_name: row.source_name, action, definition_id: action === "map" ? Number(definitionId) : null })
                .then(() => { dropFromQueue(row.source_name); setMessage(action === "map" ? `Правило создано: «${row.source_name}»` : `«${row.source_name}» — игнорируется`); return loadAttributeRules(); })
                .catch((error) => {
                    if (error?.response?.status === 409) { dropFromQueue(row.source_name); setMessage(`Поле «${row.source_name}» уже имеет правило`); return loadAttributeRules(); }
                    setMessage(error?.response?.data?.detail || "Не удалось создать правило");
                });
        };
        const patchRule = (id, data) => patchCatalogSupplierAttributeRule(id, data).then(loadAttributeRules).then(() => setMessage("Правило обновлено")).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось изменить правило"));
        const applyNow = () => runCatalogAttributeIngestion()
            .then((stats) => { setIngestStats(stats); setUnresolvedVisible(10); return Promise.all([loadUnresolvedAttributes(), loadAttributeRules()]); })
            .then(() => setMessage("Правила применены к фидам"))
            .catch((error) => setMessage(error?.response?.data?.detail || "Не удалось применить правила"));
        const refreshQueue = () => { setUnresolvedVisible(10); return Promise.all([loadUnresolvedAttributes(), loadAttributeRules()]).then(() => setMessage("Очередь обновлена")).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось обновить")); };
        const definitionOptions = attributeDefinitions.filter((d) => d.is_active);
        return <div style={{ display: "grid", gap: "16px" }}>
            <section className="cat-card" style={{ padding: "14px", display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                <div><h2 style={{ margin: 0 }}>Правила характеристик</h2><p className="cat-mute">Правило говорит: «читать поле фида X как характеристику Y» (или игнорировать техполе). Правила глобальные — действуют для всех поставщиков.</p></div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <button className="cat-btn cat-btn-primary" onClick={applyNow}>Применить правила сейчас</button>
                    <button className="cat-btn cat-btn-ghost" onClick={refreshQueue}>Обновить очередь</button>
                </div>
            </section>
            {ingestStats && <section className="cat-card" style={{ padding: "10px 14px" }}><p className="cat-mute">Последний прогон: создано характеристик в карточках {ingestStats.auto_created || 0}, обновлено {ingestStats.auto_updated || 0}, кандидатов {ingestStats.candidates_created || 0}, конфликтов {ingestStats.conflicts || 0}.</p></section>}

            <section className="cat-card" style={{ padding: "14px", display: "grid", gap: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <h3 style={{ margin: 0 }}>Действующие правила ({attributeRules.length})</h3>
                    <button className="cat-btn cat-btn-ghost" onClick={() => setRulesCollapsed((value) => !value)}>{rulesCollapsed ? "Показать" : "Скрыть"}</button>
                </div>
                {!rulesCollapsed && <>
                <p className="cat-mute">Можно изменить характеристику или действие прямо в таблице, либо отключить правило. Правила с действием «Игнорировать» — внизу.</p>
                <div className="cat-tbl-wrap"><table className="cat-tbl"><thead><tr><th>Область</th><th>Поле фида</th><th>Действие</th><th>Характеристика</th><th></th></tr></thead><tbody>
                    {[...attributeRules].sort((a, b) => (a.action === "ignore" ? 1 : 0) - (b.action === "ignore" ? 1 : 0)).map((rule) => <tr key={rule.id} style={{ opacity: rule.is_active ? 1 : 0.5 }}>
                        <td>{rule.supplier_id == null ? "Глобально" : `Поставщик ${rule.supplier_code || rule.supplier_id}`}</td>
                        <td><strong>{rule.source_name}</strong></td>
                        <td>
                            <select className="cat-inp" style={{ width: "auto", minWidth: "140px" }} value={rule.action} onChange={(event) => patchRule(rule.id, { action: event.target.value })}>
                                <option value="map">Сопоставить</option>
                                <option value="ignore">Игнорировать</option>
                                <option value="review">На проверку</option>
                            </select>
                        </td>
                        <td>
                            <select className="cat-inp" style={{ width: "auto", minWidth: "180px" }} value={rule.definition?.id || ""} disabled={rule.action !== "map"} onChange={(event) => patchRule(rule.id, { definition_id: Number(event.target.value) })}>
                                <option value="">—</option>
                                {definitionOptions.map((d) => <option key={d.id} value={d.id}>{d.name_ua}</option>)}
                            </select>
                        </td>
                        <td><button className="cat-btn cat-btn-ghost" onClick={() => patchCatalogSupplierAttributeRule(rule.id, { is_active: !rule.is_active }).then(loadAttributeRules).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось изменить правило"))}>{rule.is_active ? "Отключить" : "Включить"}</button></td>
                    </tr>)}
                    {attributeRules.length === 0 && <tr><td colSpan="5"><span className="cat-mute">Правил пока нет.</span></td></tr>}
                </tbody></table></div>
                </>}
            </section>

            <section className="cat-card" style={{ padding: "14px", display: "grid", gap: "10px" }}>
                <h3 style={{ margin: 0 }}>Очередь на обработку — полей без правила: {queue.length}</h3>
                <p className="cat-mute">Показываем по 10 самых частых. Выберите характеристику и нажмите «Создать правило», либо «Игнорировать» для техполей. Обработанное поле сразу уходит из очереди.</p>
                <div className="cat-tbl-wrap"><table className="cat-tbl"><thead><tr><th>Поле фида</th><th>Товаров</th><th>Примеры значений</th><th>Действие</th></tr></thead><tbody>
                    {shown.map((row) => <tr key={row.source_name}>
                        <td><strong>{row.source_name || "Без названия"}</strong></td>
                        <td>{row.products_count}</td>
                        <td>{(row.examples || []).join(" · ")}</td>
                        <td><div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                            <select className="cat-inp" style={{ width: "auto", minWidth: "180px" }} value={ruleTargets[row.source_name] || ""} onChange={(event) => setRuleTargets({ ...ruleTargets, [row.source_name]: event.target.value })}>
                                <option value="">Читать как характеристику…</option>
                                {definitionOptions.map((d) => <option key={d.id} value={d.id}>{d.name_ua}</option>)}
                            </select>
                            <button className="cat-btn cat-btn-primary" onClick={() => handleRule(row, "map")}>Создать правило</button>
                            <button className="cat-btn cat-btn-ghost" onClick={() => handleRule(row, "ignore")}>Игнорировать</button>
                        </div></td>
                    </tr>)}
                    {queue.length === 0 && <tr><td colSpan="4"><span className="cat-mute">Очередь пуста — все поля разобраны.</span></td></tr>}
                </tbody></table></div>
                {queue.length > unresolvedVisible && <button className="cat-btn cat-btn-ghost" onClick={() => setUnresolvedVisible((value) => value + 10)}>Показать ещё 10</button>}
            </section>
        </div>;
    };

    const renderExport = () => {
        return (
            <section className="cat-card">
                <div style={{ padding: "14px", display: "grid", gap: "12px" }}>
                    <div>
                        <h2 style={{ margin: 0 }}>Экспорт, Импорт КС</h2>
                        <p className="cat-mute" style={{ marginTop: "6px" }}>Выгрузка включает товары из snapshot выбранного Prom-кабинета, у которых в карточке ещё нет ключевых слов.</p>
                    </div>
                    <select className="cat-inp" style={{ maxWidth: "420px" }} value={aiExportAccountId} onChange={(event) => setAiExportAccountId(event.target.value)}>
                        <option value="">Выберите Prom-кабинет</option>
                        {promAccounts.map((account) => <option key={account.id} value={account.id}>{account.account_name || account.account_code} ({account.account_code})</option>)}
                    </select>
                    <button className="cat-btn cat-btn-primary" style={{ width: "fit-content" }} onClick={downloadAiEnrichmentJsonl} disabled={aiExportBusy || !aiExportAccountId}>
                        {aiExportBusy ? "Подготовка…" : "Скачать JSONL для AI"}
                    </button>
                    <div style={{ marginTop: "12px", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                        <input
                            type="file"
                            accept=".jsonl,application/x-ndjson,application/json"
                            onChange={(event) => setAiKeywordFile(event.target.files?.[0] || null)}
                        />
                        <button className="cat-btn cat-btn-primary" onClick={importAiKeywordsJsonl} disabled={aiKeywordImportBusy || !aiKeywordFile}>
                            {aiKeywordImportBusy ? "Импорт…" : "Импортировать keywords JSONL"}
                        </button>
                        <span className="cat-mute">AI-keywords будут активны в Prom сразу, со статусом «На модерации».</span>
                    </div>
                    {aiKeywordImportResult && (
                        <p className="cat-mute" style={{ marginTop: "10px" }}>
                            Строк: {aiKeywordImportResult.lines_total}; товаров: {aiKeywordImportResult.products_processed}; создано keywords: {aiKeywordImportResult.keywords_created}; дубли: {aiKeywordImportResult.duplicates_skipped}; неизвестные SKU: {aiKeywordImportResult.unknown_skus}; ошибки: {aiKeywordImportResult.validation_errors}.
                        </p>
                    )}
                </div>
            </section>
        );
    };

    const renderDrawer = () => {
        if (!drawerSku) {
            return null;
        }
        const product = drawerData?.product;
        return (
            <>
                <div className="cat-scrim" onClick={() => setDrawerSku(null)} />
                <aside className="cat-card cat-drawer">
                    <div className="cat-drawer-head">
                        {product?.main_image_url ? (
                            <img src={product.main_image_url} alt="" className="cat-thumb lg" />
                        ) : (
                            <div className="cat-thumb lg">{(product?.manufacturer || drawerSku || "?").trim().charAt(0).toUpperCase()}</div>
                        )}
                        <div className="who">
                            <p className="sku">SKU: {drawerSku}</p>
                            <h2 className="t">{textOrDash(product?.name_ua || product?.name_ru)}</h2>
                            <p className="bc">Barcode: {textOrDash(product?.barcode)}</p>
                        </div>
                        <div className="acts">
                            <span className={statusPillClass(!product?.is_archived)}>{product?.is_archived ? "В архиве" : "Активен"}</span>
                            <button className="cat-btn cat-btn-primary" onClick={() => saveProduct()}>
                                Сохранить
                            </button>
                            <button
                                className={product?.is_archived ? "cat-btn cat-btn-primary" : "cat-btn cat-btn-danger"}
                                onClick={() => {
                                    if (product?.is_archived) {
                                        saveProduct({ is_archived: false, archived_reason: "" });
                                    } else {
                                        const reason = window.prompt("Причина архивации", draft.archived_reason || "");
                                        if (reason) {
                                            saveProduct({ is_archived: true, archived_reason: reason });
                                        }
                                    }
                                }}
                            >
                                {product?.is_archived ? "Разархивировать" : "Архивировать"}
                            </button>
                            <button className="cat-drawer-close" onClick={() => setDrawerSku(null)} aria-label="Закрыть">
                                ×
                            </button>
                        </div>
                    </div>
                    <div className="cat-drawer-quality" style={{ color: product?.quality?.issues?.length ? "var(--cat-warn)" : "var(--cat-good-text)" }}>
                        Проблемы: {issueText(product?.quality)}
                    </div>
                    <div className="cat-drawer-tabs-wrap">
                        <div className="cat-tabbar">
                            {productDrawerTabs.map((tab) => (
                                <button
                                    key={tab}
                                    className={`cat-tab${drawerTab === tab ? " active" : ""}`}
                                    onClick={() => setDrawerTab(tab)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>
                    {!drawerData ? (
                        <p className="cat-drawer-body">Загрузка карточки...</p>
                    ) : (
                        <div className="cat-drawer-body">{renderDrawerTab()}</div>
                    )}
                </aside>
            </>
        );
    };

    const renderDrawerTab = () => {
        if (drawerTab === "Основное") {
            return (
                <div style={{ display: "grid", gap: "12px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
                    {[
                        ["Tabletki GUID", "tabletki_guid"],
                        ["Barcode", "barcode"],
                        ["Производитель", "manufacturer"],
                        ["Название UA", "name_ua"],
                        ["Название RU", "name_ru"],
                        ["Вес, г", "weight_g"],
                        ["Длина, мм", "length_mm"],
                        ["Ширина, мм", "width_mm"],
                        ["Высота, мм", "height_mm"],
                        ["Объем, мл", "volume_ml"],
                        ["Причина архивации", "archived_reason"],
                    ].map(([label, key]) => (
                        <Field key={key} label={label}>
                            <input className="cat-inp" value={draft[key] ?? ""} onChange={(event) => setDraft({ ...draft, [key]: event.target.value })} />
                        </Field>
                    ))}
                </div>
                <section style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", display: "grid", gap: "8px" }}>
                    <strong>Ручные значения и названия</strong>
                    <p className="cat-mute">Сохранение закрепит эти значения и импорты их не заменят.</p>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button className="cat-btn cat-btn-primary" onClick={saveManualFields}>Сохранить вручную</button>
                        {Object.keys(drawerData.manual_overrides || {}).filter((field) => ["manufacturer", "barcode", "name_ua", "name_ru", "weight_g", "length_mm", "width_mm", "height_mm", "volume_ml"].includes(field)).map((field) => (
                            <button key={field} className="cat-btn cat-btn-ghost" onClick={() => clearManualField(field)}>Вернуть обновление: {field}</button>
                        ))}
                    </div>
                </section>
                </div>
            );
        }
        if (drawerTab === "Штрихкоды") {
            return (
                <div style={{ display: "grid", gap: "12px" }}>
                    <section style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px" }}>
                        <strong>Основной ШК</strong>
                        <p className="cat-mute">{textOrDash(drawerData.product?.barcode)} · используется в выгрузках Tabletki и SalesDrive</p>
                    </section>
                    <section style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", display: "grid", gap: "8px" }}>
                        <strong>Добавить дополнительный ШК</strong>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <input className="cat-inp" value={additionalBarcodeValue} placeholder="Штрихкод" onChange={(event) => setAdditionalBarcodeValue(event.target.value)} />
                            <button className="cat-btn cat-btn-primary" onClick={async () => {
                                const value = additionalBarcodeValue.trim();
                                if (!value) return;
                                const data = await createCatalogAdditionalBarcode(drawerSku, { barcode: value });
                                setDrawerData(data); setAdditionalBarcodeValue("");
                            }}>Добавить</button>
                        </div>
                    </section>
                    {(drawerData.additional_barcodes || []).map((row) => (
                        <section key={row.id} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", display: "grid", gap: "6px" }}>
                            <strong>{row.barcode}</strong>
                            <span className="cat-mute">Источник: {row.source} · {row.is_active ? "активен" : "отключен"}</span>
                            {(row.duplicate_skus || []).length > 0 && <span className="cat-pill warn">Также используется: {row.duplicate_skus.join(", ")}. Автосопоставление пропустит этот ШК.</span>}
                            <div><button className="cat-btn cat-btn-ghost" onClick={async () => {
                                const data = await updateCatalogAdditionalBarcode(drawerSku, row.id, { is_active: !row.is_active });
                                setDrawerData(data);
                            }}>{row.is_active ? "Отключить" : "Восстановить"}</button></div>
                        </section>
                    ))}
                    {(drawerData.additional_barcodes || []).length === 0 && <p className="cat-mute">Дополнительных ШК нет.</p>}
                </div>
            );
        }
        if (drawerTab === "Контент") {
            return (
                <div style={{ display: "grid", gap: "14px" }}>
                    <Field label="Описание UA">
                        <textarea className="cat-inp" style={{ minHeight: "120px" }} value={draft.description_ua || ""} onChange={(event) => setDraft({ ...draft, description_ua: event.target.value })} />
                        <div style={{ marginTop: "6px", display: "flex", gap: "6px" }}><button className="cat-btn cat-btn-primary" onClick={() => saveManualContent("ua")}>Сохранить вручную UA</button><button className="cat-btn cat-btn-ghost" onClick={async () => { const data = await deleteManualCatalogContent(drawerSku, "ua"); setDrawerData(data); }}>Вернуть фид UA</button></div>
                    </Field>
                    <Field label="Описание RU">
                        <textarea className="cat-inp" style={{ minHeight: "120px" }} value={draft.description_ru || ""} onChange={(event) => setDraft({ ...draft, description_ru: event.target.value })} />
                        <div style={{ marginTop: "6px", display: "flex", gap: "6px" }}><button className="cat-btn cat-btn-primary" onClick={() => saveManualContent("ru")}>Сохранить вручную RU</button><button className="cat-btn cat-btn-ghost" onClick={async () => { const data = await deleteManualCatalogContent(drawerSku, "ru"); setDrawerData(data); }}>Вернуть фид RU</button></div>
                    </Field>
                    <h3>Варианты контента</h3>
                    {(drawerData.contents || []).map((row) => (
                        <div key={row.id} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px" }}>
                            <strong>{row.language_code} · {row.is_selected ? "Выбрано как основное" : "Вариант"}</strong>
                            <p className="cat-mute">Источник: {textOrDash(row.source_type)} · Поставщик: {textOrDash(row.supplier_id)}</p>
                            <p>{textOrDash(row.title)}</p>
                            <p className="cat-mute">{textOrDash(row.description)}</p>
                        </div>
                    ))}
                </div>
            );
        }
        if (drawerTab === "Ключевые слова") {
            const pendingAiCount = (drawerData.keywords || []).filter((row) => row.source_type === "ai" && row.moderation_status === "pending").length;
            const keywordRows = (languageCode) => (drawerData.keywords || []).filter((row) => row.language_code === languageCode);
            return (
                <div style={{ display: "grid", gap: "14px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                        <p className="cat-mute">Ключевые слова со статусом «На модерации» уже участвуют в Prom-выгрузке. Удаление убирает слово из следующей выгрузки.</p>
                        <button className="cat-btn cat-btn-primary" disabled={!pendingAiCount} onClick={approveAllPendingAiKeywords}>
                            Подтвердить все ({pendingAiCount})
                        </button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "14px" }}>
                        {["ua", "ru"].map((languageCode) => (
                            <section key={languageCode} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", display: "grid", gap: "10px" }}>
                                <h3 style={{ margin: 0 }}>Ключевые слова {languageCode.toUpperCase()}</h3>
                                <div style={{ display: "flex", gap: "8px" }}>
                                    <input className="cat-inp" value={keywordDrafts[languageCode] || ""} onChange={(event) => setKeywordDrafts({ ...keywordDrafts, [languageCode]: event.target.value })} placeholder="Добавить вручную через запятую" />
                                    <button className="cat-btn cat-btn-primary" onClick={() => createKeywords(languageCode)}>Добавить</button>
                                </div>
                                {keywordRows(languageCode).map((row) => (
                                    <div key={row.id} style={{ borderTop: "1px solid #eef2f7", paddingTop: "8px", display: "grid", gap: "6px" }}>
                                        <input className="cat-inp" readOnly={row.source_type === "ai"} value={keywordEdits[row.id] ?? row.keyword} onChange={(event) => setKeywordEdits({ ...keywordEdits, [row.id]: event.target.value })} />
                                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
                                            <span className="cat-mute">Источник: {row.source_type === "ai" ? "AI" : "Вручную"} · {row.platform === "prom" ? "Prom" : "Универсальное"} · {row.moderation_status === "pending" ? "На модерации" : "Подтверждено"}</span>
                                            {row.source_type !== "ai" && <>
                                                <button className="cat-btn cat-btn-ghost" onClick={() => saveKeyword(row.id)}>Сохранить</button>
                                                <button className="cat-btn cat-btn-ghost" onClick={() => moveKeyword(row, -1)}>↑</button>
                                                <button className="cat-btn cat-btn-ghost" onClick={() => moveKeyword(row, 1)}>↓</button>
                                            </>}
                                            {row.source_type === "ai" && row.moderation_status === "pending" && <button className="cat-btn cat-btn-primary" onClick={() => approveKeyword(row.id)}>Подтвердить</button>}
                                            <button className="cat-btn cat-btn-danger" onClick={async () => { await deleteCatalogProductKeyword(drawerSku, row.id); await refreshDrawer(); setMessage("Ключевое слово удалено"); }}>Удалить</button>
                                        </div>
                                    </div>
                                ))}
                                {!keywordRows(languageCode).length && <p className="cat-mute">Нет ключевых слов.</p>}
                            </section>
                        ))}
                    </div>
                </div>
            );
        }
        if (drawerTab === "Характеристики") {
            return (
                <div style={{ display: "grid", gap: "16px" }}>
                    <section style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", display: "grid", gap: "4px", backgroundColor: "#f8fafc" }}>
                        <div><span className="cat-mute">Виробник (юрособа):</span> <strong>{drawerData.product?.manufacturer || "—"}</strong></div>
                        <div className="cat-mute">Это официальный изготовитель из каталога. Торговая марка товара — в характеристике «Бренд (ТМ)» ниже; это разные поля.</div>
                    </section>
                    <section style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", display: "grid", gap: "10px" }}>
                        <h3 style={{ margin: 0 }}>Добавить характеристику</h3>
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(180px, 1.2fr) minmax(180px, 1fr) minmax(100px, 0.4fr) minmax(120px, 0.5fr) auto", gap: "8px" }}>
                            <input
                                className="cat-inp"
                                list="catalog-attribute-definitions"
                                value={attributeDraft.definition_name_ua}
                                onChange={(event) => setAttributeDraft({ ...attributeDraft, definition_name_ua: event.target.value })}
                                placeholder="Свойство"
                            />
                            <datalist id="catalog-attribute-definitions">
                                {attributeDefinitions.map((row) => <option key={row.id} value={row.name_ua}>{row.name_ru ? `${row.name_ru} · ${row.code}` : row.code}</option>)}
                            </datalist>
                            <input className="cat-inp" value={attributeDraft.values} onChange={(event) => setAttributeDraft({ ...attributeDraft, values: event.target.value })} placeholder="Значение, ещё значение" />
                            <input className="cat-inp" value={attributeDraft.unit} onChange={(event) => setAttributeDraft({ ...attributeDraft, unit: event.target.value })} placeholder="Ед." />
                            <select className="cat-inp" value={attributeDraft.value_type} onChange={(event) => setAttributeDraft({ ...attributeDraft, value_type: event.target.value })}>
                                <option value="text">Текст</option>
                                <option value="number">Число</option>
                                <option value="boolean">Да / нет</option>
                                <option value="select">Выбор</option>
                                <option value="multiselect">Несколько значений</option>
                            </select>
                            <button className="cat-btn cat-btn-primary" onClick={createAttribute}>Добавить</button>
                        </div>
                    </section>
                    <table className="cat-tbl">
                        <thead><tr><th>Свойство</th><th>Значение</th><th>Ед.</th><th>Источник</th><th>Действия</th></tr></thead>
                        <tbody>
                            {(drawerData.attributes || []).map((row) => {
                                const edit = attributeEdits[row.id] || { values: (row.values || []).join(", "), unit: row.unit || "" };
                                return (
                                    <tr key={row.id}>
                                        <td><strong>{row.definition.name_ua}</strong><div className="cat-mute">{row.definition.value_type}</div></td>
                                        <td><input className="cat-inp" value={edit.values} onChange={(event) => setAttributeEdits({ ...attributeEdits, [row.id]: { ...edit, values: event.target.value } })} /></td>
                                        <td><input className="cat-inp" value={edit.unit} onChange={(event) => setAttributeEdits({ ...attributeEdits, [row.id]: { ...edit, unit: event.target.value } })} /></td>
                                        <td>{row.source_type === "manual" ? "Вручную" : row.source_type}</td>
                                        <td><div style={{ display: "flex", gap: "6px" }}><button className="cat-btn cat-btn-ghost" onClick={() => saveAttribute(row.id)}>Сохранить</button><button className="cat-btn cat-btn-danger" onClick={async () => { await deleteCatalogProductAttribute(drawerSku, row.id); await refreshDrawer(); }}>Удалить</button></div></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {!(drawerData.attributes || []).length && <p className="cat-mute">Характеристик пока нет.</p>}
                    {(() => {
                        const pendingCandidates = (drawerData.attribute_candidates || []).filter((row) => row.status === "conflict" || row.status === "needs_review");
                        return (
                            <section style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", display: "grid", gap: "10px" }}>
                                <h3 style={{ margin: 0 }}>Требуют решения</h3>
                                <p className="cat-mute">Данные из фидов, которые не записались в карточку автоматически: разные значения от поставщиков (конфликт) или поле без правила. Уже применённые данные здесь не показываются.</p>
                                {pendingCandidates.map((row) => (
                                    <div key={row.id} style={{ borderTop: "1px solid #eef2f7", paddingTop: "8px", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                        <strong>{row.definition?.name_ua || row.attribute_name_raw || "Без названия"}</strong>
                                        <span>{(row.values || []).join(", ")}</span>
                                        <span className="cat-mute">Источник: {row.supplier_code || row.supplier_id} · {row.status === "conflict" ? "конфликт значений" : "нет правила"}</span>
                                        {row.definition && <button className="cat-btn cat-btn-ghost" onClick={async () => { const data = await actOnCatalogAttributeCandidate(drawerSku, row.id, "approve"); setDrawerData(data); }}>Записать это значение</button>}
                                        <button className="cat-btn cat-btn-danger" onClick={async () => { const data = await actOnCatalogAttributeCandidate(drawerSku, row.id, "ignore"); setDrawerData(data); }}>Игнорировать</button>
                                    </div>
                                ))}
                                {!pendingCandidates.length && <p className="cat-mute">Нет данных, требующих решения.</p>}
                            </section>
                        );
                    })()}
                </div>
            );
        }
        if (drawerTab === "Фото") {
            return (
                <div style={{ display: "grid", gap: "14px" }}>
                    <Field label="Загрузить фото">
                        <input type="file" accept="image/*" multiple onChange={(event) => uploadImages(event.target.files)} />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "12px" }}>
                        {(drawerData.images || []).map((row) => (
                            <div key={row.id} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px" }}>
                                <img src={row.image_url} alt="" style={{ width: "100%", height: "120px", objectFit: "contain", backgroundColor: "#f8fafc" }} />
                                <p className="cat-mute">{row.is_main ? "Главное" : "Фото"} · {row.is_active ? "Активно" : "Отключено"} · {row.is_marketplace_allowed ? "Разрешено для маркетплейсов" : "Не выгружается"}</p>
                                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "8px" }}>
                                    {row.is_marketplace_allowed ? (
                                        <button className="cat-btn cat-btn-danger" onClick={() => updateImageMarketplaceAccess(row, false)}>Не выгружать</button>
                                    ) : (
                                        <button className="cat-btn cat-btn-ghost" onClick={() => updateImageMarketplaceAccess(row, true)}>Разрешить</button>
                                    )}
                                    <button className="cat-btn cat-btn-ghost" disabled={!row.is_active || !row.is_marketplace_allowed || row.is_main} onClick={() => makeImageMain(row)}>Сделать главным</button>
                                    {row.source_type === "manual" && <button className="cat-btn cat-btn-danger" onClick={async () => { if (window.confirm("Удалить загруженное фото?")) { const data = await deleteCatalogImage(drawerSku, row.id); setDrawerData(data); } }}>Удалить</button>}
                                    {row.source_type === "manual" && <button className="cat-btn cat-btn-ghost" onClick={async () => { const manualIds = (drawerData.images || []).filter((item) => item.source_type === "manual" && item.is_active).map((item) => item.id); const index = manualIds.indexOf(row.id); if (index > 0) { [manualIds[index - 1], manualIds[index]] = [manualIds[index], manualIds[index - 1]]; const data = await reorderCatalogImages(drawerSku, manualIds); setDrawerData(data); } }}>↑</button>}
                                </div>
                                <a href={row.image_url} target="_blank" rel="noreferrer">Открыть оригинал</a>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
        if (drawerTab === "Категория") {
            return (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                    <div>
                        <h3>Текущий путь</h3>
                        <p>{drawerData.product?.category_path?.join(" / ") || "Категория не выбрана"}</p>
                        <Field label="Категория L1">
                            <input className="cat-inp" value={draft.category_l1_code || ""} onChange={(event) => setDraft({ ...draft, category_l1_code: event.target.value })} />
                        </Field>
                        <Field label="Категория L2">
                            <input className="cat-inp" value={draft.category_l2_code || ""} onChange={(event) => setDraft({ ...draft, category_l2_code: event.target.value })} />
                        </Field>
                    </div>
                    <div>
                        <h3>Дерево выбора</h3>
                        <CategoryTree
                            categories={categories}
                            selectedCode={draft.category_l2_code || draft.category_l1_code}
                            onSelect={(code) => {
                                const item = categoryOptions.find((option) => option.code === code);
                                setDraft({
                                    ...draft,
                                    category_l1_code: item?.level_no === 1 ? code : draft.category_l1_code,
                                    category_l2_code: item?.level_no !== 1 ? code : "",
                                });
                            }}
                        />
                    </div>
                </div>
            );
        }
        if (drawerTab === "Поставщики") {
            return (
                <table className="cat-tbl">
                    <thead>
                        <tr>
                            <th>Поставщик</th>
                            <th>Код товара</th>
                            <th>Название</th>
                            <th>Barcode</th>
                            <th>Статус</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(drawerData.mappings || []).map((row) => (
                            <tr key={row.id}>
                                <td>{textOrDash(row.supplier_name || row.supplier_id)}</td>
                                <td>{textOrDash(row.supplier_code)}</td>
                                <td>{textOrDash(row.supplier_product_name_raw)}</td>
                                <td>{textOrDash(row.barcode)}</td>
                                <td>{row.is_active ? "Активна" : "Отключена"} / {row.is_confirmed ? "Подтверждена" : "Не подтверждена"}</td>
                                <td>
                                    <button
                                        className="cat-btn cat-btn-ghost"
                                        onClick={async () => {
                                            await updateCatalogMapping(row.id, { is_confirmed: !row.is_confirmed });
                                            await loadDrawer(drawerSku);
                                        }}
                                    >
                                        {row.is_confirmed ? "Снять подтверждение" : "Подтвердить"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {drawerWholesaleMappings.map((row) => (
                            <tr key={`wholesale-${row.id}`}>
                                <td>{textOrDash(row.supplier_name)} <span className="cat-muted">(опт)</span></td>
                                <td>
                                    <input className="cat-inp" style={{ maxWidth: "150px" }} defaultValue={row.supplier_code || ""} onBlur={async (event) => {
                                        const value = event.target.value.trim();
                                        if (value && value !== row.supplier_code) {
                                            await updateCatalogWholesaleMapping(row.id, { supplier_product_code: value });
                                            await loadDrawer(drawerSku);
                                        }
                                    }} />
                                </td>
                                <td>Оптовый источник: {textOrDash(row.source)}</td>
                                <td>{textOrDash(row.barcode)}</td>
                                <td>{row.is_active ? "Активна" : "Отключена"} / {row.is_confirmed ? "Подтверждена" : "Не подтверждена"}</td>
                                <td>
                                    <button className="cat-btn cat-btn-ghost" onClick={async () => {
                                        await updateCatalogWholesaleMapping(row.id, { is_confirmed: !row.is_confirmed });
                                        await loadDrawer(drawerSku);
                                    }}>{row.is_confirmed ? "Снять подтверждение" : "Подтвердить"}</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }
        if (drawerTab === "Остатки и цены") {
            return (
                <table className="cat-tbl">
                    <thead>
                        <tr>
                            <th>Поставщик</th>
                            <th>Магазин</th>
                            <th>Остаток</th>
                            <th>Закупочная цена</th>
                                <th>Розничная цена</th>
                                <th>Дата обновления</th>
                                <th>Блокировка</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(drawerData.stock_prices || []).map((row, index) => (
                                <tr key={index}>
                                <td>{textOrDash(row.supplier_code)}</td>
                                <td>{textOrDash(row.store_name || row.store_id)}</td>
                                <td>{row.stock}</td>
                                    <td>{textOrDash(row.cost_price)}</td>
                                    <td>{textOrDash(row.retail_price)}</td>
                                    <td>{formatDate(row.updated_at)}</td>
                                    <td>{row.blocked ? row.block_reason || "Заблокировано" : "—"}</td>
                                    <td>
                                        <button
                                            className="cat-btn cat-btn-ghost"
                                            onClick={() => {
                                                setBlockDraft({ ...blockDraft, supplier_code: row.supplier_code || "" });
                                                setDrawerTab("Блокировки");
                                            }}
                                        >
                                            Заблокировать
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                );
            }
            if (drawerTab === "Блокировки") {
                return (
                    <div style={{ display: "grid", gap: "16px" }}>
                        <section style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", display: "grid", gap: "12px" }}>
                            <h3 style={{ margin: 0 }}>Добавить временную блокировку</h3>
                            <div style={{ display: "grid", gridTemplateColumns: "minmax(160px, 1fr) minmax(190px, 1fr)", gap: "12px" }}>
                                <Field label="Область">
                                    <select
                                        className="cat-inp"
                                        value={blockDraft.supplier_code}
                                        onChange={(event) => setBlockDraft({ ...blockDraft, supplier_code: event.target.value })}
                                    >
                                        <option value="">Все поставщики</option>
                                        {blockSupplierOptions.map((option) => (
                                            <option key={option.code} value={option.code}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Заблокировать до">
                                    <input
                                        className="cat-inp"
                                        type="datetime-local"
                                        value={blockDraft.blocked_until}
                                        onChange={(event) => setBlockDraft({ ...blockDraft, blocked_until: event.target.value })}
                                    />
                                </Field>
                            </div>
                            <Field label="Причина">
                                <input
                                    className="cat-inp"
                                    value={blockDraft.reason}
                                    onChange={(event) => setBlockDraft({ ...blockDraft, reason: event.target.value })}
                                />
                            </Field>
                            <Field label="Комментарий">
                                <textarea
                                    className="cat-inp"
                                    style={{ minHeight: "76px" }}
                                    value={blockDraft.comment}
                                    onChange={(event) => setBlockDraft({ ...blockDraft, comment: event.target.value })}
                                />
                            </Field>
                            <div>
                                <button className="cat-btn cat-btn-danger" onClick={() => createOfferBlock()}>
                                    Заблокировать
                                </button>
                            </div>
                        </section>
                        <section>
                            <h3>Правила блокировки</h3>
                            <table className="cat-tbl">
                                <thead>
                                    <tr>
                                        <th>Поставщик</th>
                                        <th>До</th>
                                        <th>Причина</th>
                                        <th>Статус</th>
                                        <th>Создана</th>
                                        <th>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(drawerData.offer_blocks || []).map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.supplier_code ? textOrDash(row.supplier_name || row.supplier_code) : "Все поставщики"}</td>
                                            <td>{formatDate(row.blocked_until)}</td>
                                            <td>{textOrDash(row.reason)}</td>
                                            <td>
                                                <span className={offerBlockPillClass(row.status)}>{offerBlockStatusLabel(row.status)}</span>
                                            </td>
                                            <td>{formatDate(row.created_at)}</td>
                                            <td>
                                                {row.status === "active" ? (
                                                    <button className="cat-btn cat-btn-danger" onClick={() => deactivateOfferBlock(row.id)}>
                                                        Снять
                                                    </button>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    </div>
                );
            }
            if (drawerTab === "Экспорт") {
            return (
                <div style={{ display: "grid", gap: "10px" }}>
                    {(drawerData.export_readiness || []).map((row, index) => (
                        <div key={index} style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px" }}>
                            <strong>{row.channel}: {row.status_label}</strong>
                            <p className="cat-mute">Причины: {row.reasons?.join(", ") || "Нет"}</p>
                            <p className="cat-mute">Внешний код: {textOrDash(row.external_product_code)} · Внешнее название: {textOrDash(row.external_product_name)}</p>
                            <pre style={{ whiteSpace: "pre-wrap", backgroundColor: "#f8fafc", padding: "10px", borderRadius: "6px", fontSize: "12px" }}>
                                {JSON.stringify(row.preview_payload, null, 2)}
                            </pre>
                        </div>
                    ))}
                </div>
            );
        }
        return (
            <div style={{ display: "grid", gap: "10px" }}>
                {(drawerData.quality?.issues || []).map((issue) => (
                    <div key={issue} style={{ border: "1px solid #fed7aa", backgroundColor: "#fff7ed", borderRadius: "8px", padding: "10px" }}>
                        {issue}
                    </div>
                ))}
                <p>Создано: {formatDate(drawerData.history?.created_at)}</p>
                <p>Изменено: {formatDate(drawerData.history?.updated_at)}</p>
                <p>Архивность: {drawerData.history?.is_archived ? "В архиве" : "Активен"}</p>
                <p>Причина архивации: {textOrDash(drawerData.history?.archived_reason)}</p>
            </div>
        );
    };

    return (
        <main style={pageStyle} className="catalog-page">
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: "26px" }}>Каталог</h1>
                    <p className="cat-mute">PIM-центр: товары, категории, поставщики, качество данных и экспортная готовность.</p>
                </div>
                {loading && <strong>Загрузка...</strong>}
            </div>
            {message && (
                <div className="cat-card" style={{ padding: "10px 12px", display: "flex", justifyContent: "space-between", gap: "10px" }}>
                    <span>{message}</span>
                    <button className="cat-btn cat-btn-ghost" style={{ padding: "4px 8px" }} onClick={() => setMessage("")}>
                        Закрыть
                    </button>
                </div>
            )}
            <div className="cat-subtabs">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        className={`cat-subtab${activeTab === tab.key ? " active" : ""}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            {(activeTab === "products" || activeTab === "archive") && renderProductFilters(activeTab === "archive")}
            {activeTab === "products" && renderProductsTable(false)}
            {activeTab === "archive" && renderProductsTable(true)}
            {activeTab === "categories" && renderCategories()}
            {activeTab === "attribute_normalization" && renderAttributeNormalization()}
            {activeTab === "export" && renderExport()}
            {activeTab === "blocks" && renderOfferBlocks()}
            {renderDrawer()}
        </main>
    );
};

export default CatalogPage;
